import React, { useContext } from 'react';
import { PaymentMethod } from '@stripe/stripe-js';

import { media } from 'context';
import { Clickable, Icon, Img, Layout, PillButton, Text } from 'ds';
import { selectServicesPaymentMethod } from 'store/Tenancy/selectors';
import { useAppSelector } from 'store/hooks';

import { ReactComponent as BuildingNew } from './BuildingNewLarge.svg';
import { ReactComponent as StripeBank } from 'ds/StripeBank.svg';

interface Props {
  paymentMethod?: PaymentMethod | null;
  title: string;
  type: 'services' | 'rent';
  onSelect: () => void;
}

const ServicesAndRentPaymentCard: React.FC<Props> = ({ paymentMethod, title, type, onSelect }) => {
  const servicesPaymentMethod = useAppSelector(selectServicesPaymentMethod);
  const { isMobile } = useContext(media);
  return (
    <Clickable onClick={onSelect}>
      <Layout
        width={isMobile ? '100%' : 365}
        height={isMobile ? 156 : 165}
        paddingY={isMobile ? 16 : 20}
        paddingX={isMobile ? 16 : 24}
        position="relative"
        color={!paymentMethod ? 'blue-100' : 'white'}
        borderRadius={16}
        boxShadow={paymentMethod ? '2px 0 22px 0 rgba(34.1176, 28.6275, 42.7451, 0.1200)' : undefined}
      >
        <Layout height="100%" direction="column" justify="space-between">
          <Layout direction="column" gap={12} width={215}>
            <Text size={isMobile ? 'body-md' : 'headline-sm'} align="left" semibold={isMobile}>
              {title}
            </Text>
            {!paymentMethod && (
              <Text size="body-sm" color="gray-400" align="left">
                {type === 'services'
                  ? 'Add a payment method to start using services.'
                  : 'Add a payment method to pay rent through Codi.'}
              </Text>
            )}
          </Layout>
          {paymentMethod && (
            <Layout direction="column" gap={12}>
              {paymentMethod.card ? (
                <Icon name="creditCard" size="md" color="gray-500" />
              ) : (
                <StripeBank width={30} height={30} />
              )}
              <Layout direction="row" gap={4} align="center">
                <Text size="body-sm">
                  {paymentMethod.card?.brand
                    ? paymentMethod.card.brand.charAt(0).toUpperCase() + paymentMethod.card.brand.slice(1)
                    : paymentMethod.us_bank_account?.bank_name}
                </Text>
                <Text size="body-sm">
                  {paymentMethod.us_bank_account
                    ? `•••• ${paymentMethod.us_bank_account?.last4}`
                    : `•••• ${paymentMethod.card?.last4}`}
                </Text>
                {paymentMethod.card && (
                  <Layout marginLeft={12}>
                    <Text size="body-sm">
                      {paymentMethod.card?.exp_month}/{paymentMethod.card?.exp_year}
                    </Text>
                  </Layout>
                )}
                <Layout marginLeft={12}>
                  <Text size="body-sm" color="blue-500">
                    Update
                  </Text>
                </Layout>
              </Layout>
            </Layout>
          )}
          {!paymentMethod && (
            <Layout width={184}>
              <PillButton
                icon="plus"
                iconPosition="left"
                type={
                  type === 'services' ? 'purpleGradient' : servicesPaymentMethod ? 'purpleGradient' : 'primaryInverse'
                }
                size="md"
                text={type === 'services' ? 'Add payment method' : 'Add bank account'}
              />
            </Layout>
          )}
        </Layout>
        {type === 'rent' ? (
          <Layout position="absolute" top={0} right={0}>
            <BuildingNew width={99} height={99} style={{ opacity: 0.25 }} />
          </Layout>
        ) : (
          <Layout position="absolute" top={0} right={0} width={100} height={200}>
            <Img
              alt="Codi Logo"
              srcKey="CodiLogoLargeCutOff_yw3YlQY5H.png?updatedAt=1746163746809"
              width={500}
              height={415}
              folder="ux"
            />
          </Layout>
        )}
      </Layout>
    </Clickable>
  );
};

export default ServicesAndRentPaymentCard;
